import React, { useRef, useState } from 'react';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Select, { components } from 'react-select';

import { DropdownIndicator } from 'components/application/CollectionSelect';
import Tooltip from 'components/application/Tooltip';

import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';

import {
  formatInfiniteData,
  getNextPageParam,
} from 'components/helpers/reactQuery';

export default function PersonnelTeamSelect({ filter, onOptionChange }) {
  const [isValueMenuOpen, setIsValueMenuOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const setSearchTextDebounced = useRef(
    _.debounce((searchText) => setSearchText(searchText), 500),
  ).current;

  const { hasSubordinateManagementFeatureAccess } =
    useTrainingRegisterResources();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'personnel.PersonnelTeamSelect',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const { t: i18nTComponentsShared } = useTranslation('components', {
    keyPrefix: 'personnel.shared',
  });

  const {
    data: domainTeams,
    fetchNextPage: fetchDomainTeamsNextPage,
    hasNextPage: hasDomainTeamsNextPage,
    isFetching: isDomainTeamsFetching,
  } = useInfiniteQuery({
    enabled: isValueMenuOpen || !!searchText,
    getNextPageParam,
    queryFn: async ({ pageParam = 1 }) => {
      const response = await axios.post('/team_searches', {
        team_search: { page: pageParam, search: searchText },
      });

      return response.data;
    },
    queryKey: ['teams', { search: searchText }],
    select: (data) => formatInfiniteData({ data }),
  });

  const { data: domainMetaData } = useQuery({
    enabled: isValueMenuOpen,
    queryFn: async () => {
      const [subordinatesMetaResponse, teamsMetaDataResponse] =
        await Promise.all([
          axios.get('/personnel', {
            params: { meta_only: true, only_subordinates: true },
          }),
          axios.post('/team_searches', {
            team_search: {
              filter: { only_managed_teams: true },
              meta_only: true,
            },
          }),
        ]);

      return {
        subordinates: subordinatesMetaResponse.data.meta,
        teams: teamsMetaDataResponse.data.meta,
      };
    },
    queryKey: ['personnelTeamsMetaData'],
  });

  const handleMenuOpen = () => {
    setIsValueMenuOpen(true);
  };

  const handleMenuClose = () => {
    setIsValueMenuOpen(false);
  };

  const handleChange = (selectedOption) => {
    onOptionChange(selectedOption);
  };

  const isDirectReportsOptionDisabled =
    !hasSubordinateManagementFeatureAccess ||
    domainMetaData?.subordinates.scopedCount === 0;

  const directReportsOptionDisabledReason =
    isDirectReportsOptionDisabled ?
      !hasSubordinateManagementFeatureAccess ?
        i18nT('options.yourDirectReports.disabledTooltips.lineManagerDisabled')
      : i18nT('options.yourDirectReports.disabledTooltips.noSubordinates')
    : null;

  const isYourTeamsOptionsDisabled = domainMetaData?.teams.scopedCount === 0;

  const options =
    domainTeams && domainMetaData ?
      [
        {
          options: [
            {
              label: i18nTComponentsShared('allPersonnel'),
              value: 'all_personnel',
            },
            {
              disabledReason: i18nT('options.yourTeams.disabledTooltip'),
              isDisabled: isYourTeamsOptionsDisabled,
              label: i18nT('options.yourTeams.label'),
              value: 'your_teams',
            },
            {
              disabledReason: directReportsOptionDisabledReason,
              isDisabled: isDirectReportsOptionDisabled,
              label: i18nT('options.yourDirectReports.label'),
              value: 'your_direct_reports',
            },
          ],
        },
        {
          label: i18nTCommon('shared.teams'),
          options: domainTeams.data.map((team) => ({
            label: team.attributes.name,
            value: team.id,
          })),
        },
      ]
    : [];

  return (
    <Select
      OptionDisabled={(option) => option.isDisabled}
      className='collection-select__select-container collection-select__select-container--teams'
      classNamePrefix='collection-select'
      components={{ DropdownIndicator, Option }}
      id='teamNames'
      isLoading={isDomainTeamsFetching}
      loadingMessage={() => null}
      name='teamNames'
      noOptionsMessage={() =>
        searchText ? i18nTCommon('shared.noMatchingResults') : null
      }
      onChange={handleChange}
      onInputChange={setSearchTextDebounced}
      onMenuClose={handleMenuClose}
      onMenuOpen={handleMenuOpen}
      onMenuScrollToBottom={() => {
        if (hasDomainTeamsNextPage) fetchDomainTeamsNextPage();
      }}
      openMenuOnFocus={true}
      options={options}
      placeholder={i18nTCommon('teams.searchTeams')}
      value={
        isValueMenuOpen ? '' : (
          options
            .flatMap((option) => option.options)
            .find((option) => option.value === filter.value)
        )
      }
    />
  );
}

export function Option(props) {
  return (
    <components.Option {...props}>
      <div className={classNames(props.data.isDisabled && 'tooltip-parent')}>
        <div className='collection-select__option_container tw-truncate'>
          <span>{props.children}</span>
        </div>
        {props.data.isDisabled && (
          <Tooltip
            arrow={true}
            className='tw-ml-6'
            placement='right'
            tooltip={props.data.disabledReason}
            trigger='hover'
          />
        )}
      </div>
    </components.Option>
  );
}

PersonnelTeamSelect.propTypes = {
  filter: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }).isRequired,
  onOptionChange: PropTypes.func.isRequired,
};
